<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <DspPartnerToolbar
        v-if="Object.keys(dsppartner).length !== 0"
        :dsppartner="dsppartner"
        closing
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>
              {{ $t('stats.campaign_stats') }}:
              <feather-icon
                icon="LinkIcon"
                size="20"
                class="text-primary"
              />
              <router-link
                :to="{ name: 'dashboard-user-campaign', params: { userId: campaignUserId, id: campaignId }}"
                class="text-primary"
              >
                {{ campaignName }}
              </router-link>
            </h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="handleRefresh"
              @input="toggleRange"
            />
          </div>
        </div>
      </div>
      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
      >
        <template #head(date)>
          {{ $t('stats.date') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(click)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(revenue)>
          {{ $t('stats.revenue') }}
        </template>

        <template #cell(date)="data">
          {{ data.value ? getFormattedDate(new Date(data.value)) : '' }}
        </template>
        <template #cell(impressions)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(click)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(revenue)="data">
          {{ data.value ? currency('USD', data.value) : 0 }}
        </template>

        <template #foot(date)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(click)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('click')) }}
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sum('revenue')) }}
        </template>

      </b-table>
      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import StatsDateRangeFilter from '@/views/dashboard/dsppartners/stats/StatsDateRangeFilter.vue'
import DspPartnerToolbar from '../DspPartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'

export default {
  name: 'CampaignDetails',
  components: {
    BCard,
    BTable,
    BSpinner,
    Breadcrumbs,
    DspPartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      sortBy: 'impressions',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'date', label: 'Дата', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'click', label: 'Click', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
      ],
      breadcrumbItems: [],
      dsppartner: {},
      campaignName: '',
      campaignId: '',
      campaignUserId: null,
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('dashboard-campaings-stats', ['campaignNameByIdFromStore']),
    selectedRangeFormated() {
      if (!this.rangePicker || !Array.isArray(this.rangePicker) || this.rangePicker.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        const today = getFormattedDate(new Date())
        return { from: today, to: today }
      }

      return {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }
    },
  },
  async created() {
    await this.getPartnerData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.getCampaignStatsData() // Call only once during initialization
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.getCampaignStatsData() // Call only once during initialization
    }
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getDsppartner(this.$route.params.id)
      this.dsppartner = responseData.data.dsppartner || {}
      this.campaignId = this.$route.params.campaignId
      // Get campaign name and userId through store
      try {
        await this.$store.dispatch('dashboard-campaings-stats/fetchCampaignNamesById', [this.campaignId])
        this.campaignName = this.campaignNameByIdFromStore(this.campaignId) || `Campaign ${this.campaignId}`

        // Get userId from campaign data in store
        const campaignData = this.$store.state['dashboard-campaings-stats'].campaignNames.find(c => c.id === this.campaignId)
        if (campaignData && campaignData.user_id) {
          this.campaignUserId = campaignData.user_id
        }
      } catch (error) {
        this.campaignName = `Campaign ${this.campaignId}`
      }
      this.setBreabcrumbs()
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('DSP partners'),
          to: '/dsppartners/',
        },
        {
          text: this.dsppartner.name,
          to: `/dsppartners/${this.dsppartner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          to: `/dsppartners/${this.dsppartner._id}/stats`,
        },
        {
          text: this.$t('Campaigns'),
          to: `/dsppartners/${this.dsppartner._id}/stats/campaigns`,
        },
        {
          text: this.campaignName,
          active: true,
        },
      ]
    },
    async getCampaignStatsData() {
      if (!this.campaignId) {
        return
      }

      this.isLoading = true
      try {
        const params = {
          campaign_id: this.campaignId,
          from: this.selectedRangeFormated.from,
          to: this.selectedRangeFormated.to,
          type: 'daily',
        }

        const response = await useJwt.getPhoenixDspCampaign(params)

        if (response.data && response.data.items) {
          this.rows = response.data.items.map(stat => ({
            date: stat.date,
            impressions: stat.impressions || 0,
            click: stat.clicks || 0,
            revenue: stat.revenue || 0,
          }))
        } else {
          this.rows = []
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Data Error',
            msg: 'Failed to load campaign details',
            variant: 'danger',
          },
          { root: true },
        )
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Update URL without page reload
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.currentPage = 1 // Reset to first page when changing date range
        this.getCampaignStatsData()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    handleRefresh() {
      this.getCampaignStatsData()
    },
    saveSettings() {
      try {
        localStorage.setItem('dspStatsDateFilter', JSON.stringify({ rangePicker: this.rangePicker }))
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to save date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

h4 {
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: primary;
    }
  }
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
